import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import DataTable from '../../../components/ui/DataTable';
import LoadErrorBanner from '../../../components/ui/LoadErrorBanner';
import { formatDate } from '../../../utils/Utils';
import { getCandidateApplications } from '../config/profileApi';

// Başvurular sekmesi — kişinin TÜM başvuruları (aynı LifecyclePersonId VEYA aynı
// normalize e-posta; sunucu kapsam süzgeçli). Satır → başvuru detay sayfası.
// Liste kısa (bir kişinin başvuruları) ve tek uçtan gelir: istemci tarafı tablo.
// Sıralama KAPALI — `data[rowIndex]` eşlemesi istemci sıralamasında kayardı.
export default function CandidateApplicationsTab({ candidateId }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const history = useHistory();
  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;
    setFetching(true);
    getCandidateApplications(candidateId)
      .then(({ ok, items }) => {
        if (!alive) return;
        setLoadFailed(!ok);
        setRows(items);
      })
      .finally(() => { if (alive) setFetching(false); });
    return () => { alive = false; };
  }, [candidateId]);

  const openApplication = useCallback((row) => {
    const appId = row?.applicationId || row?.id;
    if (appId) history.push(`/ats/applications/${appId}`);
  }, [history]);

  const columns = useMemo(() => [
    {
      name: 'jobPostingTitle',
      label: t('ats.candidateProfile.jobPosting'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'requisitionTitle',
      label: t('ats.candidateProfile.requisition'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'status',
      label: t('ats.candidateProfile.status'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => (v ? t(`ats.application.status.${v}`, { defaultValue: v }) : '-'),
      },
    },
    {
      name: 'currentStageName',
      label: t('ats.candidateProfile.stage'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'scoreAggregate',
      label: t('ats.candidateProfile.score'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => (v == null ? '-' : Number(v).toFixed(1)),
      },
    },
    {
      name: 'appliedAt',
      label: t('ats.candidateProfile.appliedAt'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatDate(v, lang) },
    },
    {
      name: 'hireSkipped',
      label: t('ats.candidateProfile.hireSkipped'),
      options: {
        filter: false,
        sort: false,
        // Sunucu işe alımın atlanma NEDENİNİ kod olarak taşır (başvuru listesindeki
        // rozetle aynı sözlük: ats.hire.skipped.*); bilinmeyen kod "diğer"e düşer.
        customBodyRender: (v) => {
          if (!v) return '-';
          if (typeof v !== 'string') return t('ats.hire.skipped.other');
          return t(`ats.hire.skipped.${v}`, { defaultValue: t('ats.hire.skipped.other') });
        },
      },
    },
  ], [t, lang]);

  return (
    <div data-role="candidate-applications">
      {loadFailed && <LoadErrorBanner message={t('ats.candidateProfile.applicationsLoadFailed')} />}
      <DataTable
        title={t('ats.candidateProfile.applicationsTitle')}
        fetching={fetching}
        columns={columns}
        data={rows}
        totalRowCount={rows.length}
        serverSide={false}
        disableFilters
        onRowClick={openApplication}
      />
    </div>
  );
}
